<script>
    import { onMount } from "svelte";

    import {
        PageHead,
        PageLoading,
        Tab,
        UploadPdfModal,
        Loader,
        NotificationModal,
    } from "../../Components/Common/index.svelte";

    import {
        getDoctorStatus,
        getText,
        getTitle,
        goBack,
        replaceURL,
        showError,
        showException,
        showInfo,
    } from "../../Utils/utilFunctions.svelte";

    import { apiMethods } from "../../Services/index.svelte";

    import { user } from "../../stores";

    import AccountInfoTab from "./components/AccountInfoTab.svelte";
    import AccountDetailsTab from "./components/AccountDetailsTab.svelte";
    import AccountDoctorTab from "./components/AccountDoctorTab.svelte";
    import AccountBillingRuleAndPresetsTab from "./components/AccountBillingRuleAndPresetsTab.svelte";
    import AccountCalendarTab from "./components/AccountCalendarTab.svelte";
    import AccountCalendarEventTab from "./components/AccountCalendarEventTab.svelte";
    import AccountInsightsTab from "./components/AccountInsightsTab.svelte";

    import {
        defaultCalendarEventColor,
        headerRightBtnCls,
    } from "../../Utils/utilConstants.svelte";

    import {
        checkIfShowTab,
        isEnvNewOrApp,
    } from "../../Utils/utilRoute.svelte";

    import {
        DirectDoctorStatus,
        doctorStatus,
    } from "../../Utils/utilEnums.svelte";

    import AccountCommunicationTab from "./components/AccountCommunicationTab.svelte";

    export let params = {};

    let isGettingAccount = false,
        isSendingOnboardingForm = false,
        showOnboardingFormSentModal = false,
        isUploadAccountSchedulePdfModalOpen = false,
        account = {},
        backupAccount = {},
        usersUnderAccount = [],
        invoicesUnderAccount = [],
        providersUnderAccount = [],
        isEditEnabled = false,
        isUpdatingDoctor = false;

    let clearSignatureModalOpen = false,
        isCancellingSignature = false,
        clearSignatureDone = false;

    let currentTab = "";

    $: isLoading = isGettingAccount;

    $: isUserAllowSendOnboardingForms = $user.role >= 20;

    $: isUserAllowUploadSchedulePdf = $user.role >= 2;

    $: showSendOnboardingFormBtn =
        backupAccount.status == 0 || backupAccount.status == 1;

    $: showClearSignatureBtn = isEnvNewOrApp();

    $: isUserAdmin = $user.role == 100;

    $: isUserManager = $user.role == 80;

    onMount(async () => {
        if (!checkIfShowTab($user.role, params.root)) {
            goBack();
            return;
        }

        await loadDoctorById(params.id);

        if (!params.query) currentTab = "accountInfo";
        else _syncTab(params.query);
    });

    const loadDoctorById = async (id) => {
        isGettingAccount = true;

        await apiMethods.getMethod
            .getAccountById(id)
            .then((res) => {
                if (res.error) showError(res.error);
                else _handleAccount(res);
            })
            .catch((err) => showException(err));

        isGettingAccount = false;
    };

    const reload = () => {
        if (params.id) loadDoctorById(params.id);
    };

    const silentReload = (id) => {
        apiMethods.getMethod
            .getAccountById(id)
            .then((res) => {
                if (res.error) showError(res.error);
                else {
                    account = { ...res };
                    backupAccount = { ...res };
                }
            })
            .catch((err) => showException(err));
    };

    const switchTabs = (tab = "") => {
        if (!tab) return;

        if (tab == "accountInfo") delete params.query;

        currentTab = tab;

        replaceURL(`/Account/view/${params.id}/tab=${tab}`);
    };

    const cancelEdit = () => {
        isEditEnabled = false;
        console.log("cancelled edit");
        account = { ...backupAccount };
    };

    const saveDoctor = async ({ detail }) => {
        isUpdatingDoctor = true;
        await apiMethods.putMethod
            .updateDoctor({
                ...account,
                profile: { ...detail },
            })
            .then((res) => {
                if (res.id) {
                    showInfo("Account details updated");
                    silentReload(res.id);
                } else {
                    showError(res?.error);
                }
            })
            .catch((err) => {
                showException(err);
            });
        isUpdatingDoctor = false;
        isEditEnabled = false;
        isChangingStatusOptionsOpen = false;
    };

    const sendOnboardingForm = async () => {
        isSendingOnboardingForm = true;

        await apiMethods.postMethod
            .sendAccountOnboardingForm({ accountId: Number(params.id) })
            .then((res) => {
                if (res.toString() == "true") {
                    showOnboardingFormSentModal = true;

                    loadDoctorById(params.id);
                } else
                    showError(
                        "Failed to send onboarding form, please try again later or contact support",
                    );
            });

        isSendingOnboardingForm = false;
    };

    // HELPER FUNCTIONS
    const _handleAccount = async (res) => {
        if (res.error) showError(res.error);
        else {
            if (!res.calendarColour)
                res.calendarColour = defaultCalendarEventColor;
            account = { ...res };
            backupAccount = { ...res };
            usersUnderAccount = [...res.users];
            providersUnderAccount = [...res.providers];

            providersUnderAccount = providersUnderAccount.map((provider) => ({
                ...provider,
                doctorId: res.id,
                accountGroupId: account.groupId,
            }));

            await apiMethods.getMethod
                .getAccountInvoice(0, 999999, {
                    "filter[doctorId]": res.id,
                })
                .then((aiRes) => {
                    if (aiRes.error) showError(aiRes.error);
                    else invoicesUnderAccount = [...aiRes.results];
                });
        }
    };

    const _syncTab = (query) => {
        const arr = query.split("&");

        for (let i = 0; i < arr.length; i++) {
            if (arr[i].includes("tab=")) currentTab = arr[i].split("=")[1];
        }
    };

    const _openClearSignatureModal = () => (clearSignatureModalOpen = true);

    const _closeClearSignatureModal = () => (clearSignatureModalOpen = false);

    const _closeOnboardingFormSentModal = () => {
        showOnboardingFormSentModal = false;
    };

    const _closeUploadAccountSchedulePdfModal = () => {
        isUploadAccountSchedulePdfModalOpen = false;
    };

    let statusContainerEle,
        newAccountStatus,
        isStatusChangeMenuOpen = false,
        isChangingStatusOptionsOpen = false;

    const _openConfirmUpdateEncounterStatusModal = (id) => {
        if (isEditEnabled || account.status == id) return;
        newAccountStatus = id;
        isChangingStatusOptionsOpen = true;
    };

    const cancelUpdateStatus = () => {
        isChangingStatusOptionsOpen = false;
    };

    const updateAccountStatus = async () => {
        account.status = newAccountStatus;
        isUpdatingDoctor = true;
        await apiMethods.putMethod
            .updateDoctor(account)
            .then((res) => {
                if (res.error) showError(res?.error);
                else {
                    if (
                        res.id === 0 &&
                        newAccountStatus === DirectDoctorStatus.Deleted
                    ) {
                        showInfo("Doctor deleted");
                        history.go(-1);
                    } else {
                        showInfo("Doctor status updated");
                        silentReload(res.id);
                    }
                }
            })
            .catch((err) => {
                showException(err);
            });
        isUpdatingDoctor = false;
        isEditEnabled = false;
        isChangingStatusOptionsOpen = false;
    };

    const handleClearSignature = async () => {
        isCancellingSignature = true;

        await apiMethods.getMethod
            .getAccountAvatarWithType(account.id, 1)
            .then(async (res) => {
                if (!res || res.error)
                    showError(
                        res.error ??
                            "Failed to get account signatures, please try again later or contact support...",
                    );
                else {
                    await deleteSignatures(res.results);
                    clearSignatureDone = true;
                }
            });

        isCancellingSignature = false;

        _closeClearSignatureModal();
    };

    const deleteSignatures = async (signatures = []) => {
        if (!signatures.length) return;

        const batchApiCallNumber = 5;

        let finished = 0,
            total = signatures.length;

        while (finished < total) {
            let tempNum = 0,
                promises = [];

            while (tempNum < batchApiCallNumber) {
                const curr = signatures[finished + tempNum];

                if (!curr) {
                    tempNum++;
                    continue;
                }

                promises.push(
                    apiMethods.deleteMethod.deleteAccountAvatar(curr.id),
                );

                tempNum++;
            }

            await Promise.all(promises);
            finished += promises.length;
        }
    };
</script>

{#if isLoading}
    <PageLoading />
{:else}
    <div class="flex w-full justify-between">
        <div class="flex w-full items-center gap-x-4 mb-2">
            <PageHead
                title={getTitle(account.title) +
                    " " +
                    getText(account.firstName) +
                    " " +
                    getText(account.lastName) +
                    `${
                        account.preferredName
                            ? " (" + account.preferredName + ")"
                            : ""
                    }`}
            />
            {#if account.signatureApproved}
                <div class="badge badge-secondary py-2 cursor-default">
                    Signature approved
                </div>
            {/if}

            {#if account.isMfaEnabled}
                <div class="badge badge-secondary py-2 cursor-default">Mfa</div>
            {/if}

            <!-- {#if account.isNewProvider}
                <div class="badge badge-secondary py-2 cursor-default">
                    New Provider
                </div>
            {/if} -->

            <div class="flex flex-1 justify-end gap-x-4 gap-y-2 flex-wrap">
                <div class="flex flex-row gap-x-2">
                    <div
                        class="flex-none"
                        on:mouseleave={() => (isStatusChangeMenuOpen = false)}
                    >
                        <div class="menu menu-horizontal">
                            <li
                                tabindex="0"
                                on:mousemove={() =>
                                    !isEditEnabled &&
                                    (isStatusChangeMenuOpen = true)}
                            >
                                <div
                                    class="flex items-center {headerRightBtnCls} {isStatusChangeMenuOpen
                                        ? 'rounded-t-lg'
                                        : 'rounded-lg'} {isEditEnabled
                                        ? 'cursor-default'
                                        : ''}"
                                >
                                    {#if isUpdatingDoctor}
                                        <Loader size="1rem" />
                                    {/if}
                                    <p>
                                        {getDoctorStatus(account.status)}
                                    </p>
                                    <svg
                                        class="fill-current"
                                        xmlns="http://www.w3.org/2000/svg"
                                        width="20"
                                        height="20"
                                        viewBox="0 0 24 24"
                                        ><path
                                            d="M7.41,8.58L12,13.17L16.59,8.58L18,10L12,16L6,10L7.41,8.58Z"
                                        /></svg
                                    >
                                </div>

                                <ul
                                    bind:this={statusContainerEle}
                                    class="z-[1000] dropdown-content menu border-0 bg-zinc-300 w-48 rounded-b-lg overflow-hidden {isStatusChangeMenuOpen
                                        ? 'flex'
                                        : 'hidden'}"
                                >
                                    <div>
                                        {#each doctorStatus as { id, type }}
                                            <div
                                                class={`flex items-center justify-start p-4 gap-x-2 border-0 h-12 cursor-pointer ${id == account.status ? "bg-secondary text-white" : "bg-zinc-200 text-[#6f726c] hover:bg-zinc-300"}`}
                                                on:click|preventDefault={() =>
                                                    _openConfirmUpdateEncounterStatusModal(
                                                        id,
                                                    )}
                                            >
                                                {type}
                                            </div>
                                        {/each}
                                    </div>
                                </ul>
                            </li>
                        </div>
                    </div>
                </div>

                {#if showClearSignatureBtn}
                    <button
                        class={headerRightBtnCls}
                        on:click={_openClearSignatureModal}
                        disabled={isCancellingSignature}
                    >
                        <i class="fa-solid fa-eraser"></i>
                        {isCancellingSignature
                            ? "Processing"
                            : "Clear Signature"}
                    </button>
                {/if}

                {#if isUserAllowSendOnboardingForms && showSendOnboardingFormBtn}
                    <button
                        class={headerRightBtnCls}
                        on:click={sendOnboardingForm}
                        disabled={isSendingOnboardingForm}
                    >
                        <i class="fa fa-paper-plane"></i>
                        {isSendingOnboardingForm
                            ? "Sending"
                            : "Send Onboarding Forms"}
                    </button>
                {/if}

                {#if isUserAllowUploadSchedulePdf}
                    <button
                        class={headerRightBtnCls}
                        on:click={() =>
                            (isUploadAccountSchedulePdfModalOpen = true)}
                        disabled={isSendingOnboardingForm}
                    >
                        <i class="fa fa-cloud-upload"></i>
                        Upload List PDF
                    </button>
                {/if}
            </div>
        </div>
    </div>

    <div class="flex flex-1 flex-col shadow-2xl rounded-lg bg-white">
        <div class="flex items-end m-4 flex-wrap">
            <Tab
                {currentTab}
                displayTab="Account Info"
                tabUrl="accountInfo"
                clickTabFunc={() => switchTabs("accountInfo")}
            />

            <Tab
                {currentTab}
                displayTab="Account Details"
                tabUrl="accountDetails"
                clickTabFunc={() => switchTabs("accountDetails")}
            />

            <Tab
                {currentTab}
                displayTab="Surgeons / Referrers"
                tabUrl="accountDoctors"
                clickTabFunc={() => switchTabs("accountDoctors")}
            />

            <Tab
                {currentTab}
                displayTab="Billing Rules & Presets"
                tabUrl="accountBillingRuleAndPresets"
                clickTabFunc={() => switchTabs("accountBillingRuleAndPresets")}
            />

            <Tab
                {currentTab}
                displayTab="Calendar"
                tabUrl="accountCalendar"
                clickTabFunc={() => switchTabs("accountCalendar")}
            />

            <Tab
                {currentTab}
                displayTab="Calendar Events"
                tabUrl="accountCalendarEvent"
                clickTabFunc={() => switchTabs("accountCalendarEvent")}
            />

            <Tab
                {currentTab}
                displayTab="Communication"
                tabUrl="communication"
                clickTabFunc={() => {
                    switchTabs("communication");
                }}
            />

            <Tab
                {currentTab}
                displayTab="Insights"
                tabUrl="accountInsight"
                clickTabFunc={() => switchTabs("accountInsight")}
                isHidden={!isUserAdmin && !isUserManager}
            />
        </div>

        {#if currentTab == "accountInfo"}
            <AccountInfoTab
                bind:doctorDetails={account}
                bind:isEditEnabled
                bind:isUpdatingDoctor
                on:saveDoctor={saveDoctor}
                on:cancelEdit={cancelEdit}
                userDetails={usersUnderAccount}
                invoicesDetails={invoicesUnderAccount}
                {reload}
            />
        {:else if currentTab == "accountDetails"}
            <AccountDetailsTab
                providerDetails={providersUnderAccount}
                accountId={account.id}
                accountGroupId={account.groupId}
                {reload}
            />
        {:else if currentTab == "accountInsight" && (isUserAdmin || isUserManager)}
            <AccountInsightsTab accountId={account.id} {params} />
        {:else if currentTab == "accountCalendar"}
            <AccountCalendarTab
                accountId={account.id}
                eventColour={account.calendarColour}
                {params}
            />
        {:else if currentTab == "accountCalendarEvent"}
            <AccountCalendarEventTab accountId={account.id} {params} />
        {:else if currentTab == "accountDoctors"}
            <AccountDoctorTab accountId={account.id} {params} />
        {:else if currentTab == "accountBillingRuleAndPresets"}
            <AccountBillingRuleAndPresetsTab
                {params}
                bind:doctorDetails={account}
            />
        {:else if currentTab == "communication"}
            <AccountCommunicationTab accountId={account.id} />
        {/if}
    </div>
{/if}

<UploadPdfModal
    isModalOpen={isUploadAccountSchedulePdfModalOpen}
    closeModal={_closeUploadAccountSchedulePdfModal}
    accountId={params.id}
    showDateInput
    showSurgeonSelector
/>

<NotificationModal
    title="Clear Signature?"
    isModalOpen={clearSignatureModalOpen}
    closeModal={_closeClearSignatureModal}
    closeModalFunction={_closeClearSignatureModal}
    showCancelBtn={false}
    showConfirmBtn={false}
    btnOptions={[
        {
            text: "Cancel",
            class: "btn btn-accent",
            loading: isCancellingSignature,
            function: () => _closeClearSignatureModal(),
        },
        {
            text: isCancellingSignature ? "Processing" : "Clear",
            class: "btn btn-error",
            loading: isCancellingSignature,
            function: async () => await handleClearSignature(),
        },
    ]}
>
    <p class="leading-6 p-4">Reset the account signature?</p>
</NotificationModal>

<div
    class={showOnboardingFormSentModal
        ? "modal modal-open sm:modal-middle"
        : "modal"}
>
    <div class="modal-box">
        <h3 class="font-medium text-lg">Done</h3>
        <div class="p-4">
            <p class="leading-6 text-secondary whitespace-pre-line">
                Onboarding forms sent successfully.
            </p>
        </div>
        <div class="modal-action">
            <button
                class="btn btn-secondary text-white btn-sm border-none w-24"
                on:click={_closeOnboardingFormSentModal}
            >
                Close
            </button>
        </div>
    </div>
</div>

<NotificationModal
    bind:isModalOpen={isChangingStatusOptionsOpen}
    confirmAction={updateAccountStatus}
    closeModal={cancelUpdateStatus}
    closeModalFunction={cancelUpdateStatus}
    title="Update Doctor Status"
    isConfirming={isUpdatingDoctor}
    isConfirmDisabled={isEditEnabled}
    confirmButtonClass={newAccountStatus == DirectDoctorStatus.Blocked ||
    newAccountStatus == DirectDoctorStatus.Deleted
        ? "btn-error"
        : "btn-secondary"}
    cancelButtonClass="btn-accent"
    submitText="Confirm"
    cancelText="Cancel"
>
    Change the status of this doctor to {getDoctorStatus(newAccountStatus)}?
</NotificationModal>

<NotificationModal
    isModalOpen={clearSignatureDone}
    title="Signature Cleared"
    showCancelBtn={false}
    submitText="Close"
    closeModalFunction={() => (clearSignatureDone = false)}
    confirmAction={() => (clearSignatureDone = false)}
>
    <p class="leading-6 p-4">
        The account signature was cleared and the user will be forced to provide
        it the next time they use the app.
    </p>
</NotificationModal>
